import type {
  ClientRelationWorklog,
  ClientRelationWorklogDetail,
  DayTrackedStatsGridRow,
  TrackedTimeForDayDetails,
} from './models';

type ParseResult = { data: TrackedTimeForDayDetails; error: null } | { data: null; error: string };

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isOptionalString = (value: unknown): value is string | null | undefined =>
  value === undefined || value === null || typeof value === 'string';

const isOptionalNumber = (value: unknown): value is number | null | undefined =>
  value === undefined || value === null || isFiniteNumber(value);

const isWorklogDetail = (value: unknown): value is ClientRelationWorklogDetail => {
  if (!isObject(value)) return false;

  return (
    isFiniteNumber(value.CrmWorklogDetailsId) &&
    isFiniteNumber(value.CrmWorkLogId) &&
    isOptionalString(value.StartTime) &&
    isOptionalString(value.EndTime) &&
    isOptionalNumber(value.MouseClicks) &&
    isOptionalNumber(value.KeyBoardPressed) &&
    isOptionalString(value.Screenshot) &&
    isOptionalString(value.ScreenshotPreview) &&
    isOptionalNumber(value.MouseMoves) &&
    typeof value.Overtime === 'boolean' &&
    (value.OvertimeHours === undefined ||
      value.OvertimeHours === null ||
      isFiniteNumber(value.OvertimeHours))
  );
};

const isWorklog = (value: unknown): value is ClientRelationWorklog => {
  if (!isObject(value)) return false;

  return (
    isFiniteNumber(value.CrmWorkLogId) &&
    isFiniteNumber(value.CrmRelationId) &&
    isOptionalString(value.Description) &&
    isOptionalString(value.ProjectName) &&
    isOptionalString(value.LogStart) &&
    isOptionalString(value.LogEnd) &&
    isOptionalNumber(value.Hours) &&
    typeof value.IsLoggedAutomatically === 'boolean' &&
    typeof value.HasOvertime === 'boolean' &&
    Array.isArray(value.WorkLogDetails) &&
    value.WorkLogDetails.every(isWorklogDetail)
  );
};

const isTrackedTimeDetails = (value: unknown): value is TrackedTimeForDayDetails => {
  if (!isObject(value)) return false;

  return (
    typeof value.Date === 'string' &&
    !Number.isNaN(Date.parse(value.Date)) &&
    isFiniteNumber(value.Total) &&
    isFiniteNumber(value.TrackedWithTracker) &&
    isFiniteNumber(value.TrackedManually) &&
    isFiniteNumber(value.Overtime) &&
    Array.isArray(value.WorkLogs) &&
    value.WorkLogs.every(isWorklog)
  );
};

export const parseDayTrackedStatsRow = (
  row: Record<string, unknown> | DayTrackedStatsGridRow
): ParseResult => {
  const detailInfo = row.DetailInfo;
  if (detailInfo === undefined || detailInfo === null || detailInfo === '') {
    return { data: null, error: 'Tracked-time details are unavailable for this day.' };
  }

  let parsed: unknown = detailInfo;
  if (typeof detailInfo === 'string') {
    try {
      parsed = JSON.parse(detailInfo);
    } catch {
      return { data: null, error: 'Tracked-time details could not be read.' };
    }
  }

  if (!isTrackedTimeDetails(parsed)) {
    return { data: null, error: 'Tracked-time details are unavailable or invalid.' };
  }

  return { data: parsed, error: null };
};
